import { brandByHostname, type BrandDefinition, type BrandKey } from "@school-sales-ai/shared";

/**
 * The brand of a screen that has no client to ask.
 *
 * ONLY the anonymous screens use this — login, the auth callback and the public
 * privacy notice. Everywhere behind authentication the brand comes from the
 * CLIENT (`GET /crm/branding`), which is what makes logging in on the "wrong"
 * domain a non-event: whichever door you come through, the CRM re-brands itself
 * to your school's brand the moment it knows who you are.
 *
 * Unknown hosts — localhost, Vercel previews, the project's default
 * `*.vercel.app` domain — fall back to the platform brand rather than erroring.
 */
export function brandFromLocation(): BrandDefinition {
  return brandByHostname(window.location.hostname);
}

/**
 * The brand's square mark, as a path under `public/`.
 *
 * A total `Record`, so adding a brand fails to COMPILE until its asset is named —
 * the alternative (a fallback to the default brand's file) would render the wrong
 * mark beside a correct wordmark, which reads as a bug in the logo rather than a
 * missing file.
 *
 * The CRM can serve these from `public/` because it owns its origin. The widget
 * cannot — it is mounted on the school's page — which is why it draws the mark as
 * inline SVG (`BrandMark`) instead.
 */
const BRAND_MARK_SRC: Record<BrandKey, string> = {
  scholaops: "/favicon.svg",
  general: "/brand/loxago/favicon.svg",
};

export function brandMarkSrc(brand: BrandDefinition): string {
  return BRAND_MARK_SRC[brand.key];
}

/**
 * What a client's catalog entries are called on screen.
 *
 * A school sells "programas" and a shop sells "productos", and the CRM chrome is
 * the CLIENT's brand (§6.13), so the word follows the same key. A total `Record`
 * for the same reason as `BRAND_MARK_SRC`: a new brand must not compile until
 * someone has chosen its word — falling back to the school's would print
 * "programa" on a hardware dealer's reports.
 */
export type CatalogNoun = {
  singular: string;
  plural: string;
  Singular: string;
  Plural: string;
};

const CATALOG_NOUN: Record<BrandKey, CatalogNoun> = {
  scholaops: { singular: "programa", plural: "programas", Singular: "Programa", Plural: "Programas" },
  general: { singular: "producto", plural: "productos", Singular: "Producto", Plural: "Productos" },
};

export function catalogNoun(brand: BrandDefinition): CatalogNoun {
  return CATALOG_NOUN[brand.key];
}
